import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  countryState,
  toggleCountry,
  toggleRegion,
  toJobGeoFields,
  type CountryMeta,
  type GeoSelection,
} from '@/features/geo/geo-selection';
import { useCategorySearch, useCountries, useCreateJob, useEstimate, useRegions } from '@/lib/api/queries';
import type { Category } from '@/lib/api/types';
import { cn } from '@/lib/cn';
import { formatInt } from '@/lib/format';

function Wizard() {
  const navigate = useNavigate();
  const { data: countries } = useCountries();
  const [selection, setSelection] = useState<GeoSelection>({});
  const [openCountry, setOpenCountry] = useState<string | null>(null);
  const { data: regions } = useRegions(openCountry);
  const [search, setSearch] = useState('');
  const { data: categories } = useCategorySearch(search);
  const [chosen, setChosen] = useState<Category[]>([]);
  const [name, setName] = useState('');
  const [exactMatch, setExactMatch] = useState(false);
  const createJob = useCreateJob();

  const geo = useMemo(() => toJobGeoFields(selection), [selection]);

  // L'estimation ne consomme aucune requête RapidAPI : on la relance à
  // chaque changement de sélection, c'est le garde-fou avant de dépenser.
  const estimateBody = geo.country_codes.length > 0 && chosen.length > 0
    ? {
        category_ids: chosen.map((c) => c.id),
        country_codes: geo.country_codes,
        admin1_codes: geo.admin1_codes,
        exact_match: exactMatch,
      }
    : null;
  const { data: estimate } = useEstimate(estimateBody);

  const canLaunch = estimateBody !== null && estimate?.fits_in_quota === true;

  const launch = () => {
    if (!estimateBody) return;
    createJob.mutate(
      {
        ...estimateBody,
        name: name.trim() || `${chosen[0]?.name_en ?? 'Run'} — ${geo.country_codes.join(', ')}`,
        filters: {},
        max_results_per_unit: null,
      },
      {
        onSuccess: (job) => {
          toast.success('Run lancé');
          void navigate({ to: '/runs/$runId', params: { runId: job.id } });
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      <div className="flex min-w-0 flex-1 flex-col gap-6">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Nouveau run</h1>
          <p className="text-sm text-secondary">
            Choisissez une zone et un métier. L'estimation est gratuite.
          </p>
        </div>

        <section className="rounded-card bg-surface p-5 shadow-card">
          <h2 className="mb-3 font-bold">1 · Zone géographique</h2>
          <div className="flex flex-col gap-2">
            {countries?.map((country) => {
              const state = countryState(selection, country.code);
              const meta: CountryMeta = {
                code: country.code, name: country.name, unitCount: country.unitCount,
              };
              return (
                <div key={country.code} className="rounded-tile border border-hairline p-3">
                  <div className="flex items-center justify-between gap-3">
                    <label className="flex min-w-0 items-center gap-3">
                      <input
                        type="checkbox"
                        checked={state === 'all'}
                        ref={(el) => {
                          if (el) el.indeterminate = state === 'partial';
                        }}
                        onChange={() => setSelection((s) => toggleCountry(s, meta.code))}
                        className="size-4"
                      />
                      <span className="truncate font-medium">{country.name}</span>
                      <span className="shrink-0 text-xs tabular-nums text-tertiary">
                        {formatInt(country.unitCount)} zones
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setOpenCountry(openCountry === country.code ? null : country.code)}
                      className="shrink-0 text-xs font-semibold text-accent"
                    >
                      {openCountry === country.code ? 'Masquer' : 'Détailler'}
                    </button>
                  </div>

                  {openCountry === country.code && regions ? (
                    <div className="mt-3 grid gap-1 border-t border-hairline pt-3 sm:grid-cols-2">
                      {regions.map((region) => (
                        <label key={region.code} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={(() => {
                              const entry = selection[country.code];
                              if (!entry) return false;
                              return entry.mode === 'all'
                                || entry.regions.includes(region.code);
                            })()}
                            onChange={() =>
                              setSelection((s) => toggleRegion(s, country.code, region.code,
                                regions.map((r) => ({
                                  code: r.code,
                                  name: r.name ?? r.code,
                                  unitCount: r.unitCount,
                                }))))}
                            className="size-4"
                          />
                          <span className="truncate">{region.name ?? region.code}</span>
                          <span className="ml-auto shrink-0 text-xs tabular-nums text-tertiary">
                            {formatInt(region.unitCount)}
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>

        <section className="rounded-card bg-surface p-5 shadow-card">
          <h2 className="mb-3 font-bold">2 · Métier</h2>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher une catégorie (ex. plumber)…"
            className="w-full rounded-control border border-hairline px-3 py-2 text-sm"
          />
          {chosen.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {chosen.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => setChosen((c) => c.filter((x) => x.id !== category.id))}
                  className="rounded-full bg-tint-blue px-3 py-1 text-xs font-semibold text-accent"
                >
                  {category.name_en} ✕
                </button>
              ))}
            </div>
          ) : null}
          {categories && categories.length > 0 ? (
            <ul className="mt-3 max-h-56 overflow-auto rounded-tile border border-hairline">
              {categories.map((category) => (
                <li key={category.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setChosen((c) => (c.some((x) => x.id === category.id) ? c : [...c, category]));
                      setSearch('');
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-sunken"
                  >
                    {category.name_en}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <label className="mt-4 flex items-start gap-3 border-t border-hairline pt-4 text-sm">
            <input
              type="checkbox"
              checked={exactMatch}
              onChange={(e) => setExactMatch(e.target.checked)}
              className="mt-0.5 size-4"
            />
            <span>
              <span className="font-medium">Correspondance exacte</span>
              <span className="mt-0.5 block text-xs text-secondary">
                Met la catégorie entre guillemets. Resserre les résultats sur le
                métier visé, au prix de quelques fiches voisines en moins.
              </span>
            </span>
          </label>
        </section>

        <section className="rounded-card bg-surface p-5 shadow-card">
          <h2 className="mb-3 font-bold">3 · Nom du run</h2>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Plombiers Île-de-France"
            className="w-full rounded-control border border-hairline px-3 py-2 text-sm"
          />
        </section>
      </div>

      <aside className="w-full shrink-0 lg:w-80">
        <div className="sticky top-6 rounded-card bg-surface p-5 shadow-card">
          <h2 className="font-bold">Estimation</h2>
          <p className="mt-1 text-xs text-secondary">
            Gratuite : elle ne consomme aucune requête.
          </p>
          {estimate ? (
            <dl className="mt-4 flex flex-col gap-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-secondary">Zones</dt>
                <dd className="font-semibold tabular-nums">{formatInt(estimate.units)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-secondary">Appels estimés</dt>
                <dd className="font-semibold tabular-nums">{formatInt(estimate.estimated_calls)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-secondary">Quota après</dt>
                <dd className={cn('font-semibold tabular-nums',
                  estimate.fits_in_quota ? '' : 'text-danger')}>
                  {formatInt(estimate.quota_remaining_after)}
                </dd>
              </div>
              {!estimate.fits_in_quota ? (
                <p className="rounded-control bg-tint-pink px-3 py-2 text-xs text-danger">
                  Ce run dépasse le quota du mois. Réduisez la zone ou attendez la
                  prochaine période.
                </p>
              ) : null}
            </dl>
          ) : (
            <p className="mt-4 text-sm text-secondary">
              Sélectionnez au moins une zone et un métier.
            </p>
          )}

          <button
            type="button"
            disabled={!canLaunch || createJob.isPending}
            onClick={launch}
            className={cn(
              'mt-5 w-full rounded-control px-4 py-2.5 text-sm font-bold text-white transition-colors',
              canLaunch ? 'bg-accent' : 'cursor-not-allowed bg-tertiary',
            )}
          >
            {createJob.isPending ? 'Lancement…' : 'Lancer le run'}
          </button>
        </div>
      </aside>
    </div>
  );
}

export const Route = createFileRoute('/new')({ component: Wizard });
